<template>
  <h6 v-if="item.inputType === 'heading'" class="question-heading mt-1">{{ item.label }}</h6>

  <div v-else-if="hidden" class="d-none"></div>

  <b-form-group v-else-if="item.inputType === 'checkbox'" class="question-field">
    <validation-provider #default="{ errors }" :rules="rules" :name="validationName">
      <b-form-checkbox :checked="!!value" :disabled="disabled" :state="errors.length ? false : null" @change="emit($event)">
        {{ labelText }}
      </b-form-checkbox>
      <small v-if="item.help" class="text-muted d-block">{{ item.help }}</small>
      <small class="text-danger">{{ errors[0] }}</small>
    </validation-provider>
  </b-form-group>

  <b-form-group v-else :label="labelText" class="question-field">
    <validation-provider #default="{ errors }" :rules="rules" :name="validationName">
      <b-form-textarea
        v-if="item.inputType === 'textarea'"
        :value="value"
        :rows="item.rows || 2"
        :placeholder="item.placeholder || ''"
        :disabled="disabled"
        :state="errors.length ? false : null"
        @input="emit($event)"
      />
      <b-form-radio-group
        v-else-if="item.inputType === 'radio' || item.builtinKey === 'clientType'"
        :checked="value"
        :options="options"
        :disabled="disabled"
        :stacked="stacked"
        :class="{ 'mt-50': !stacked }"
        @change="emit($event)"
      />
      <b-form-select
        v-else-if="item.inputType === 'select'"
        :value="value === undefined ? null : value"
        :options="selectOptions"
        :disabled="disabled"
        :state="errors.length ? false : null"
        @change="emit($event)"
      />
      <b-form-checkbox-group
        v-else-if="item.inputType === 'checkboxes'"
        :checked="Array.isArray(value) ? value : []"
        :options="options"
        :disabled="disabled"
        stacked
        @change="emit($event)"
      />
      <b-form-input
        v-else
        :value="value"
        :type="inputType"
        :placeholder="item.placeholder || ''"
        :disabled="disabled"
        :state="errors.length ? false : null"
        @input="emit($event)"
        @blur="$emit('blur')"
      />
      <small v-if="lockedNote" class="text-muted d-block">{{ lockedNote }}</small>
      <small v-if="item.help" class="text-muted d-block">{{ item.help }}</small>
      <small class="text-danger">{{ errors[0] }}</small>
    </validation-provider>
  </b-form-group>
</template>

<script>
// One question of the form schema. `value` is the stored answer; `input` carries the new
// one. The parent decides where the answer lives (dataKey) so this component stays dumb.
import { ValidationProvider } from "vee-validate";
import { required, email } from "@validations";
import { effectiveRequired, isHidden, rulesFor } from "./formSchema";

export default {
  name: "QuestionField",

  components: { ValidationProvider },

  props: {
    item: { type: Object, required: true },
    value: { default: null },
    form: { type: Object, default: () => ({}) },
    person: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    name: { type: String, default: null },
    lockedNote: { type: String, default: null },
  },

  data() {
    return { required, email };
  },

  computed: {
    hidden() {
      return isHidden(this.item, this.form, this.person);
    },
    isRequired() {
      return effectiveRequired(this.item, this.form, this.person);
    },
    labelText() {
      return this.item.label + (this.isRequired ? " *" : "");
    },
    validationName() {
      return this.name || this.item.label;
    },
    rules() {
      return rulesFor(this.item, this.form, this.person);
    },
    options() {
      return (this.item.options || []).map((option) => (typeof option === "object" ? option : { text: option, value: option }));
    },
    selectOptions() {
      return [{ text: "Please select…", value: null }].concat(this.options);
    },
    stacked() {
      return this.options.some((option) => String(option.text).length > 40) || this.options.length > 4;
    },
    inputType() {
      return ["date", "number", "email", "tel"].indexOf(this.item.inputType) !== -1 ? this.item.inputType : "text";
    },
  },

  methods: {
    emit(value) {
      this.$emit("input", value);
    },
  },
};
</script>
